import { ButtonProps, Stack } from '@mui/material';
import {
    Children,
    cloneElement,
    isValidElement,
    ReactElement,
    ReactNode,
} from 'react';
import { ModalSizingVariant } from './Modal.styleUtils';
import { SlotProvider } from '@upheal/design-system/utils/SlotContext';
import { Slots } from '@upheal/design-system/utils/Slots';
import { useSlotContext } from '@upheal/design-system/utils/useSlotContext';

const spacingValues = {
    sm: 1.5,
    md: 2.5,
} as const satisfies Record<ModalSizingVariant, number>;

interface ModalActionButtonsProps {
    children: ReactNode;

    /**
     * If true, the buttons will take the full width on tablet and desktop (sm+).
     * On mobile (xs), buttons are always full width and stacked.
     * @default false
     */
    fullWidth?: boolean;
}

export const ModalActionButtons = ({
    children,
    fullWidth,
}: ModalActionButtonsProps) => {
    const { sizing = 'md' } = useSlotContext({}, 'modal');

    const slots: Slots = {
        button: {
            size: sizing,
            sx: {
                width: { xs: '100%', sm: fullWidth ? undefined : 'auto' },
                flex: { sm: fullWidth ? 1 : undefined },
            },
        },
    };

    return (
        <SlotProvider value={slots}>
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={spacingValues[sizing]}
                sx={{
                    width: fullWidth ? '100%' : 'auto',
                    maxWidth: '100%',
                    alignItems: { xs: 'stretch', sm: 'center' },
                    justifyContent: fullWidth ? 'space-between' : 'flex-end',
                }}
            >
                {Children.map(children, (child) => {
                    if (isValidElement(child)) {
                        return cloneElement<ButtonProps>(
                            child as ReactElement<ButtonProps>,
                            { slot: 'button' },
                        );
                    }
                    return child;
                })}
            </Stack>
        </SlotProvider>
    );
};
